import React, {useMemo, useState} from 'react'
import {Column, flexRender, Header, RowData,} from '@tanstack/react-table'
import {useSortable,} from '@dnd-kit/sortable'
import {CSS} from '@dnd-kit/utilities'
import {EmblaIcon} from "../emblaIcon/emblaIcon";
import {EmblaIcons, EmblaIconsType} from "../../emblaIcons";
import classNames from "classnames";
import {TableHead} from "../../cnComponents/cnTable";
import './ReactTableStyles.scss';
import {getCommonHeaderStyles} from "./ReactTableUtilities";
import {ReactComponent as DragAndDrop} from "../../../content/icons/dragAndDrop.svg";
import {createPortal} from "react-dom";
import TableContextMenu from "./TableContextMenu";
import {Localizer} from "../../../infrastructure/localization/localizer";
import {SearchNumberField} from "../search/searchNumberField";
import SearchFieldControlled from "../search/searchField";
import {Dropdown, DropdownOption} from "../dropdown/dropdown";
import {MultiValue, SingleValue} from "react-select";

declare module '@tanstack/react-table' {
    //allows us to define custom properties for our columns
    interface ColumnMeta<TData extends RowData, TValue> {
        filterVariant?: 'text' | 'range' | 'select' | 'multiselect'
    }
}

type ReactTableHeaderProps<T> = {
    header: Header<T, unknown>
    enableColumnGrouping?: boolean;
    enableColumnResizing?: boolean;
}

const ReactTableHeader = <T, >({
                                   header,
                                   enableColumnGrouping,
                                   enableColumnResizing
                               }: ReactTableHeaderProps<T>) => {
    const {attributes, isDragging, listeners, setNodeRef, transform} =
        useSortable({
            id: header.column.id,
        });

    const [isHovered, setIsHovered] = useState(false);

    const sortIcons: Record<string, EmblaIconsType> = {
        asc: EmblaIcons.ArrowDown,
        desc: EmblaIcons.ArrowUp,
    };

    const getSortIcon = (sortState?: string | boolean): EmblaIconsType => {
        return typeof sortState === 'string'
            ? sortIcons[sortState] ?? null
            : EmblaIcons.Sort;
    };

    return (
        <TableHead key={header.id}
                   onMouseEnter={() => setIsHovered(true)}
                   onMouseLeave={() => setIsHovered(false)}
                   colSpan={header.colSpan}
                   ref={setNodeRef}
                   style={{...getCommonHeaderStyles(header.column, isDragging, CSS.Translate.toString(transform), false)}}
        >
            {header.isPlaceholder
                ? null
                :
                <div className={"flex-container-row align-items-center margin-top-s truncate"}>
                    <div className={classNames("wrap flex-container-row flex-grow")}>
                        {isHovered && !header.column.getIsPinned() && !header.column.getIsGrouped() &&
                            <button
                                style={{cursor: 'move', left: '0', height: '25px', position: 'absolute', opacity: 1}}
                                className={classNames("margin-right-s btn btn-svg border-0 shadow-none drag-and-drop margin-left-m")} {...attributes} {...listeners}>
                                <DragAndDrop/>
                            </button>
                        }
                        <div
                            className={classNames("flex-container-row flex-grow flex-fill align-items-center fit-content-min-width", header.column.getCanSort() ? 'column-sort-cursor' : '')}
                            style={{width: header.column.getSize()}}>
                            <div onClick={header.column.getToggleSortingHandler()}>
                                {flexRender(header.column.columnDef.header, header.getContext())}
                                {header.column.getCanSort() ? <EmblaIcon
                                    iconName={getSortIcon(header.column.getIsSorted()) as EmblaIconsType}
                                    additionalClasses="small"/> : <></>}
                            </div>
                            {!header.column.getCanPin() && !header.column.getCanGroup()
                                ? null
                                :
                                <div className={"flex-align-r"}>
                                    <div id={header.column.id}>
                                        <EmblaIcon iconName={EmblaIcons.Overflow}/>
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                </div>
            }
            {header.column.getCanFilter() ? (
                <Filter key={header.column.id} column={header.column}/>
            ) : null}
            {createPortal(
                <TableContextMenu header={header} enableColumnGrouping={enableColumnGrouping}/>
                , document.body
            )}
            {enableColumnResizing && isHovered &&
                <div
                    {...{
                        onDoubleClick: () => header.column.resetSize(),
                        onMouseDown: header.getResizeHandler(),
                        onTouchStart: header.getResizeHandler(),
                        className: `resizer ${
                            header.column.getIsResizing() ? 'isResizing' : ''
                        }`,
                    }}
                />
            }
        </TableHead>
    )
}

const Filter = ({column}: { column: Column<any> }) => {
    const {filterVariant} = column.columnDef.meta ?? {}
    const columnFilterValue = useMemo(() => column.getFilterValue(), [column.getFilterValue()])

    const getOptionString = (option: any): string => {
        if (option === true)
            return Localizer.global_ja();
        else if (option === false)
            return Localizer.global_nej();
        else return option;
    }

    const sortedUniqueValues = React.useMemo(
        () =>
            filterVariant !== 'select' && filterVariant !== 'multiselect'
                ? []
                : Array.from(column.getFacetedUniqueValues().keys())
                    .sort()
                    .slice(0, 50)
                    .map(value => ({value, label: getOptionString(value)})),
        [column.getFacetedUniqueValues()]
    )

    const setSelectFilterValue = (value?: string) => {
        if (value === Localizer.global_ja())
            column.setFilterValue(true);
        else if (value === Localizer.global_nej())
            column.setFilterValue(false);
        else
            column.setFilterValue(value)
    }

    const handleMultiDropdownChoice = (selectedOption: MultiValue<DropdownOption<string>> = []) => {
        const selectedValues = selectedOption.map(option => option.value);
        selectedValues.length > 0
            ? column.setFilterValue(selectedValues)
            : column.setFilterValue(undefined);
    };

    const handleSingleDropdownChoice = (selectedOption: SingleValue<DropdownOption<string>>) => {
        setSelectFilterValue(selectedOption?.value);
    };

    return filterVariant === 'range'
        ?
        (
            <div className="flex-container-row flex-space-between">
                <SearchNumberField
                    additionalClasses={"margin-top-s margin-bottom-s"}
                    initialValue={(columnFilterValue as [number | undefined, number | undefined])?.[0]}
                    onChange={value =>
                        column.setFilterValue((old: [number | undefined, number | undefined]) => [value, old?.[1]])
                    }
                    placeholder={`${column.getFacetedMinMaxValues()?.[0] !== undefined
                        ? `${Localizer.global_min()} (${column.getFacetedMinMaxValues()?.[0].toFixed(0)})`
                        : ''}`}
                    timeDelay={500}
                    minVal={column.getFacetedMinMaxValues()?.[0]}
                    maxVal={column.getFacetedMinMaxValues()?.[1]}
                />
                <SearchNumberField
                    additionalClasses={"margin-top-s margin-bottom-s"}
                    initialValue={(columnFilterValue as [number | undefined, number | undefined])?.[1]}
                    onChange={(value) => column.setFilterValue((old: [number | undefined, number | undefined]) => [old?.[0], value])}
                    placeholder={`${column.getFacetedMinMaxValues()?.[1]
                        ? `${Localizer.global_max()} (${column.getFacetedMinMaxValues()?.[1].toFixed(0)})`
                        : ''}`}
                    timeDelay={500}
                    minVal={column.getFacetedMinMaxValues()?.[0]}
                    maxVal={column.getFacetedMinMaxValues()?.[1]}
                />
            </div>
        )
        :
        filterVariant === 'multiselect'
            ?
            (
                <Dropdown
                    className={"margin-top-s margin-bottom-s"}
                    placeholder={Localizer.global_vaelg()}
                    options={sortedUniqueValues}
                    onChange={(newVal) => handleMultiDropdownChoice(newVal)}
                    value={sortedUniqueValues.filter(x => (columnFilterValue as string[])?.includes(x.value))}
                    isClearable
                    renderMultiValuesAsText
                    isMulti
                />
            )
            :
            filterVariant === 'select'
                ? (
                    <Dropdown
                        className={"margin-top-s margin-bottom-s"}
                        placeholder={Localizer.global_vaelg()}
                        options={sortedUniqueValues}
                        onChange={(newVal) => handleSingleDropdownChoice(newVal)}
                        value={sortedUniqueValues.filter(x => (columnFilterValue as string) === x.value)}
                        isClearable
                    />
                )
                :
                filterVariant === 'text'
                    ?
                    (
                        <SearchFieldControlled
                            additionalClasses={"margin-top-s margin-bottom-s"}
                            initialValue={(columnFilterValue ?? '') as string}
                            callback={(value) => column.setFilterValue(value)}
                            placeholder={`${Localizer.global_soeg()}`}
                            timeDelay={500}
                        />
                    )
                    :
                    <></>
}
export default ReactTableHeader;
